import { AttachInternals, Component, Element, Event, EventEmitter, h, Host, Method, Prop, State } from '@stencil/core'

import { CssClassMap } from '../../../utils/interfaces'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/step?tab=designer).
 *
 * @slot content-area - Slot para conteúdo customizado dentro do botão do item do step.
 *
 * @part label - Parte para o contêiner do texto da etapa, usada para estilizar tipografia e cor via ::part().
 *
 */
@Component({
  tag: 'br-step-item',
  styleUrl: 'step-item.scss',
  shadow: true,
  formAssociated: true,
})
export class StepItem {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el: HTMLBrStepItemElement

  @AttachInternals() elementInternals: ElementInternals

  /**
   *  Propriedade que define o conteúdo do texto auxiliar de realce do item de Step.
   */
  @Prop({ reflect: true }) label: string = ''

  /**
   *  Propriedade que define o tipo de ícone de destaque exibido pelo item Step, com quatro opções disponíveis: "success", "info", "danger" e "warning".
   */
  @Prop({ reflect: true }) highlight?: 'success' | 'info' | 'danger' | 'warning'

  /**
   *  Propriedade que define se o item Step está com estado ativo.
   */
  @Prop({ reflect: true }) active: boolean = false

  /**
   *  Propriedade que define se o item Step está com estado desativado.
   */
  @Prop({ reflect: true }) disabled: boolean = false

  /**
   *  Propriedade Define o nome do ícone do componente BRIcon que será exibido como conteúdo do step item, propriedade funciona apenas quando o state contentType assume o valor br-icon.
   */
  @Prop({ reflect: true }) brIconName?: string = ''

  /**
   *  Propriedade Define o nome do ícone o texto de acessibilidade apresentando quando o tipo de conteúdo for o br-icon
   */
  @Prop({ reflect: true }) brIconAria: string = ''

  @State() showTimeLine: boolean = false
  @State() stepItemPositionStatus: 'first' | 'middle' | 'last' = 'first'
  @State() stepLayout: 'horizontal' | 'vertical' = 'horizontal'
  @State() labelPosition: 'top' | 'right' | 'bottom' | 'left' = 'bottom'
  @State() contentType: 'default' | 'br-icon' | 'no-content' | 'slotted' = 'default'
  @State() mode: 'controller' | 'step' = 'step'
  @State() content: string = ''
  @State() ariaLabelContent: string = ''

  /**
   * Emite um evento após o componente ter sido carregado pela primeira vez.
   * Utilize este evento para realizar ações que devem ocorrer antes que o componente seja renderizado ou atualizado.
   * Consulte a documentação do [Stencil](https://stenciljs.com/docs/component-lifecycle#componentdidload) para mais detalhes.
   */
  @Event() brWillRender: EventEmitter<void>
  componentWillRender() {
    let accessibilityContent = ''
    let activeItem = ''
    if (this.contentType === 'slotted') {
      const slotedContent = this.el.lastElementChild
      accessibilityContent =
        this.label !== ''
          ? `passo ${this.content} ${slotedContent.textContent} ${this.label} ${this.highLightAriaLabel[this.highlight]}`
          : `passo ${this.content} ${slotedContent.textContent} ${this.highLightAriaLabel[this.highlight]}`
      this.ariaLabelContent = accessibilityContent
      return
    }
    if (this.contentType === 'br-icon') {
      accessibilityContent =
        `passo  ${this.content} ` + this.brIconAria + this.label + this.highLightAriaLabel[this.highlight]
      activeItem = this.active === true ? accessibilityContent + ' selecionado' : accessibilityContent
      this.ariaLabelContent = activeItem
      return
    } else {
      accessibilityContent =
        this.label !== ''
          ? `passo ${this.content} ${this.label} ${this.highLightAriaLabel[this.highlight]}`
          : `passo ${this.content} ${this.highLightAriaLabel[this.highlight]}`
      activeItem = this.active === true ? accessibilityContent + ' selecionado' : accessibilityContent
      this.ariaLabelContent = activeItem
    }
  }

  /**
   * Emite um evento após o componente ter sido carregado pela primeira vez.
   * Utilize este evento para realizar ações que devem ocorrer antes que o componente seja renderizado ou atualizado.
   * Consulte a documentação do [Stencil](https://stenciljs.com/docs/component-lifecycle#componentdidload) para mais detalhes.
   */
  @Event() brDidRender: EventEmitter<void>
  componentDidRender() {
    this.brDidRender.emit()
  }

  /**
   *
   * Método disponibilizado via api do elemento que indica a posição do step item, ele serve para setar o state stepItemPositionStatus do componente
   */
  @Method()
  async setStepItemPositionStatus(value: 'first' | 'middle' | 'last') {
    this.stepItemPositionStatus = value
  }

  /**
   * Método que define a orientação do stepItem e controla a exibição da linha do tempo após o componente.
   */
  @Method()
  async handleShowTimeLine(value: boolean, layout) {
    this.showTimeLine = value
    this.stepLayout = layout
  }

  /**
   *
   * Método disponibilizado via api do elemento que atribui o valor ao estado que define onde o label, o texto de destaque, irá ficar localizado
   */
  @Method()
  async setLabelPosition(value: 'top' | 'right' | 'bottom' | 'left') {
    this.labelPosition = value
  }

  /**
   *
   * Método disponibilizado via api do elemento que atribui o valor que irá definir o tipo de conteúdo apresentado dentro do componente stepItem
   */
  @Method()
  async setContentType(value: 'default' | 'br-icon' | 'no-content' | 'slotted') {
    this.contentType = value
  }

  /**
   *
   * Método disponibilizado via api do elemento que atribui o valor que irá definir o modo como o componente irá se comportar e exibir seu estilo.
   */
  @Method()
  async setMode(value: 'controller' | 'step') {
    this.mode = value
  }
  /**
   *
   * Método disponibilizado via api do elemento que atribui o valor que irá ser exibido dentro do componente stepItem
   */
  @Method()
  async setContent(value: string) {
    this.content = value
  }

  private getAlertClass(): CssClassMap {
    return {
      'step-alert': true,
      'no-content': this.contentType === 'no-content',
    }
  }

  private highLightAriaLabel = {
    success: 'passo completo',
    info: 'passo com item informativo',
    warning: 'passo com alerta',
    danger: 'passo com erro',
    undefined: '',
  }

  private IconsName = {
    success: 'fa-check',
    info: 'fa-info-circle',
    warning: 'fa-exclamation-triangle',
    danger: 'fa-times',
  }

  render() {
    return (
      <Host
        data-label={this.labelPosition}
        data-type={this.contentType === 'no-content' ? 'void' : undefined}
        class={{
          [this.stepLayout]: true,
          'mode-controller': this.mode === 'controller',
          'disabled-host': this.disabled,
          timeline: this.showTimeLine,
        }}
      >
        <button
          class={{
            'step-progress-btn': true,
            active: this.active,
          }}
          type="button"
          role="option"
          aria-selected={this.active ? 'true' : 'false'}
          aria-label={this.ariaLabelContent}
          disabled={this.disabled}
          step-num={this.contentType === 'default' ? this.content : undefined}
          data-alert={this.highlight || undefined}
        >
          {this.label && (
            <span class="step-info" part="label">
              {this.label}
            </span>
          )}

          {/* Ícone de conteúdo (quando contentType é br-icon) */}
          {this.contentType === 'br-icon' && (
            <span class="step-icon">
              <br-icon icon-name={this.brIconName} aria-hidden="true"></br-icon>
            </span>
          )}

          {/* Slot para conteúdo customizado */}
          {this.contentType === 'slotted' && (
            <span class="step-icon">
              <slot name="content-area"></slot>
            </span>
          )}

          {/* Ícone de alerta/highlight */}
          {this.mode === 'step' && this.highlight && (
            <div class={this.getAlertClass()}>
              <br-icon
                icon-name={this.IconsName[this.highlight]}
                class={this.highlight === 'warning' ? 'warning-color' : ''}
              ></br-icon>
            </div>
          )}
        </button>
      </Host>
    )
  }
}
